import type { ConductionResults } from '@wix/wix-petri-client';
import { filter } from 'ramda';
import type { Category, CategoryOverride } from '../../../types/categories';
import type { Group, GroupOverride } from '../../../types/groups';
import { removeUnusedEntities } from '../removeUnused';
import type { AnySection, SectionItemBase, SectionItemOverrideBase, SectionOverride } from '../../../types/sections';
import { filterOutEntitiesList } from '../filterOutEntitiesList';
import { isWithConditions } from '../conditions';
import { applyOverridesFactory } from '../overrides';
import type { BaseClientEntities, PartialBaseEntities } from '../../../fetchData';
import type { PanelDesign } from '../../../types/panelDesigns';

/**
 * overrides without conditions are already applied on server.
 * there's no need to return them to client
 */
const getOverridesForClient = filter(isWithConditions);

const applyOverrides = applyOverridesFactory({ isApplyingOverridesOnClient: false });

/**
 * We have all entities downloaded from CMS.
 * Now we need to filter them, apply overrides, remove unused entities and return to client.
 */
export const filterOutEntities = (
  entities: PartialBaseEntities,
  filterParams: { experiments: ConductionResults; locale: string },
): BaseClientEntities => {
  // first we need to filter all entities.
  const filteredCategories = filterOutEntitiesList<Category>(entities.categories as Category[], filterParams);
  const filteredCategoriesOverrides = filterOutEntitiesList<CategoryOverride>(
    entities.categoriesOverrides as CategoryOverride[],
    filterParams,
  );
  const filteredGroups = filterOutEntitiesList<Group>(entities.groups as Group[], filterParams);
  const filteredGroupsOverrides = filterOutEntitiesList<GroupOverride>(
    entities.groupsOverrides as GroupOverride[],
    filterParams,
  );
  const filteredSections = filterOutEntitiesList<AnySection>(entities.sections as AnySection[], filterParams);
  const filteredSectionsOverrides = filterOutEntitiesList<SectionOverride>(
    entities.sectionsOverrides as SectionOverride[],
    filterParams,
  );
  const filteredItems = filterOutEntitiesList<SectionItemBase>(entities.items as SectionItemBase[], filterParams);
  const filteredItemsOverrides = filterOutEntitiesList<SectionItemOverrideBase>(
    entities.itemsOverrides as SectionItemOverrideBase[],
    filterParams,
  );
  const panelDesigns = filterOutEntitiesList<PanelDesign>(entities.panelDesigns as PanelDesign[], filterParams);

  // apply categories overrides
  // entity = category
  // child = group
  const [categories, groupsAfterCategoriesOverrides, finalCategoriesOverrides] = applyOverrides<
    Category,
    CategoryOverride,
    Group
  >({
    entityList: filteredCategories,
    overridesList: filteredCategoriesOverrides,
    overrideToEntityReferenceFieldName: 'categoryToOverride',
    childEntityList: filteredGroups,
    childToEntityMultiReferenceFieldName: 'categoryIds',
    childToOverrideMultiReferenceFieldName: 'categoriesOverridesIds',
  });

  // apply groups overrides
  // parent = category
  // entity = group
  // child = section
  const [groups, sectionsAfterGroupsOverrides, finalGroupsOverrides] = applyOverrides<Group, GroupOverride, AnySection>(
    {
      entityList: groupsAfterCategoriesOverrides,
      overridesList: filteredGroupsOverrides,
      overrideToEntityReferenceFieldName: 'groupToOverride',
      childEntityList: filteredSections,
      childToEntityMultiReferenceFieldName: 'groupIds',
      childToOverrideMultiReferenceFieldName: 'groupOverrideIds',
    },
  );

  // apply sections overrides
  // parent = group
  // entity = section
  // child = item
  const [sections, itemsAfterSectionsOverrides, finalSectionsOverrides] = applyOverrides<
    AnySection,
    SectionOverride,
    SectionItemBase
  >({
    entityList: sectionsAfterGroupsOverrides,
    overridesList: filteredSectionsOverrides,
    overrideToEntityReferenceFieldName: 'sectionToOverride',
    childEntityList: filteredItems,
    childToEntityMultiReferenceFieldName: 'sectionIds',
    childToOverrideMultiReferenceFieldName: 'sectionOverrideIds',
    overrideToParentMultiReferenceFieldName: 'applyWhenInTheseGroups',
    entityToParentMultiReferenceFieldName: 'groupIds',
  });

  // apply items overrides
  // parent = section
  // entity = item
  const [items, , finalItemsOverrides] = applyOverrides<SectionItemBase, SectionItemOverrideBase, any>({
    entityList: itemsAfterSectionsOverrides,
    overridesList: filteredItemsOverrides,
    overrideToEntityReferenceFieldName: 'itemToOverride',
    childEntityList: [],
    childToEntityMultiReferenceFieldName: '',
    childToOverrideMultiReferenceFieldName: '',
    entityToParentMultiReferenceFieldName: 'sectionIds',
  });

  return removeUnusedEntities({
    categories,
    categoriesOverrides: getOverridesForClient(finalCategoriesOverrides),
    groups,
    groupsOverrides: getOverridesForClient(finalGroupsOverrides),
    sections,
    sectionsOverrides: getOverridesForClient(finalSectionsOverrides),
    items,
    itemsOverrides: getOverridesForClient(finalItemsOverrides),
    panelDesigns,
  });
};
